import type { PostingFit } from "@/lib/api/resumeService";
import { writeJobLocation, type JobLocation } from "@/lib/jobLocation";
import { writeJobsView, type JobsView } from "./view";

/** The address for a view, with the place searched kept as it was. */
export function jobsParams(
    view: JobsView,
    browser: readonly string[],
    place: JobLocation | null,
): URLSearchParams {
    const next = writeJobsView(view, browser);
    const location = writeJobLocation(new URLSearchParams(), place);
    for (const [key, value] of location) next.set(key, value);
    return next;
}

/** The postings whose applications and fits are asked for: the page's, and one opened from elsewhere. */
export function askedIds(
    postings: readonly { posting_id: string }[],
    elsewhere: { posting_id: string } | null | undefined,
): string[] {
    return [
        ...postings.map((posting) => posting.posting_id),
        ...(elsewhere ? [elsewhere.posting_id] : []),
    ];
}

/**
 * The posting the drawer shows: the one chosen, on this page or fetched
 * on its own. With none chosen, the first shown opens beside the list
 * until the drawer is closed. On a phone the drawer sits under the list,
 * so nothing opens there by itself.
 */
export function drawerPosting<Shown extends { posting_id: string }>(state: {
    chosen: string | null;
    onPage: Shown | undefined;
    elsewhere: Shown | undefined;
    shown: readonly Shown[];
    closed: boolean;
    beside: boolean;
}): Shown | null {
    if (!state.beside) return null;
    const first = !state.chosen && !state.closed ? state.shown[0] : undefined;
    return state.onPage ?? state.elsewhere ?? first ?? null;
}

/** A posting's fit: null when there is no resume to score, undefined while it is unknown. */
export function fitIn(
    fits: Readonly<Record<string, PostingFit>> | null | undefined,
    postingId: string,
): PostingFit | null | undefined {
    return fits === null ? null : fits?.[postingId];
}
